// src/context/AuthContext.js
/**
 * Global authentication context.
 *
 * Exposes:
 *   • `user`    Firebase user object (or `null` when signed-out).
 *   • `loading` Flag while auth state is being resolved on boot.
 *
 * DI-0093: Prolonged sign-in with secure session handling
 * ------------------------------------------------------
 * We configure Firebase Auth persistence *before* subscribing to auth state.
 * This allows us to enforce a consent-driven policy:
 *
 *   • Consent unknown (default) -> do NOT persist across browser restarts.
 *   • Essential storage accepted -> persist across browser restarts.
 *
 * Security posture
 * ----------------
 * • We do not store tokens ourselves (no localStorage/cookie token handling).
 * • We rely on Firebase Auth persistence modes and refresh handling.
 * • We "fail closed" (non-persistent) if consent is unknown or persistence
 *   configuration fails.
 *
 * Implementation notes:
 *   1. `onAuthStateChanged` fires once on mount and whenever the user's
 *      authentication state changes.
 *   2. The user object is *optimistically* set as soon as Firebase returns it.
 *      We then **enrich** the object with a `returning` flag based on Cloud
 *      Storage metadata, without blocking the UI.
 *   3. `contextValue` is memoised to prevent needless re-renders.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getAuth,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import { getStorage, list, ref } from "firebase/storage";
import { firebaseApp } from "../firebase/firebaseApp";
import { useConsent } from "./ConsentContext"; // <-- add this context

// Provide sensible defaults to avoid undefined checks in consumers.
const AuthContext = createContext({
  user: null,
  loading: true,
});

/**
 * Derive an Auth persistence mode from cookie/storage consent.
 *
 * Policy:
 * - Unknown consent -> session-only (safe default; survives reloads, not restarts).
 * - Essential accepted -> local persistence (survives browser restarts).
 * - Essential explicitly declined -> in-memory (most strict; clears on refresh).
 *
 * @param {{ status: "unknown" | "set", essential?: boolean }} consent
 * @returns {import("firebase/auth").Persistence}
 */
function getPersistenceForConsent(consent) {
  if (!consent || consent.status !== "set") return browserSessionPersistence;
  if (consent.essential) return browserLocalPersistence;
  return inMemoryPersistence;
}

/**
 * React provider that supplies `user` and `loading` to its subtree.
 */
export function AuthProvider({ children }) {
  // Firebase SDK factories are idempotent; they return singletons.
  const auth = getAuth(firebaseApp);

  // Consent is managed separately (also sets you up for analytics gating later).
  const consent = useConsent();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ------------------------------------------------------------------ */
  /* Configure persistence + subscribe to auth state.                    */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    let unsubscribe = null;
    let cancelled = false;

    (async () => {
      // Default: safe / non-persistent until consent is explicitly set.
      const desiredPersistence = getPersistenceForConsent(consent);

      try {
        // Must be configured before attaching onAuthStateChanged.
        await setPersistence(auth, desiredPersistence);
      } catch (err) {
        // Fail closed: use a safe non-persistent behaviour if config fails.
        if (process.env.NODE_ENV === "development") {
          console.error("Auth persistence configuration failed:", err);
        }
      }

      if (cancelled) return;

      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!firebaseUser) {
          setUser(null);
          setLoading(false);
          return;
        }

        // Expose bare user immediately for snappy UI updates.
        setUser(firebaseUser);
        setLoading(false);

        // Fetch a "returning" flag asynchronously.
        try {
          const storage = getStorage(firebaseApp);
          const metadataRef = ref(storage, `user_metadata/${firebaseUser.uid}`);
          const res = await list(metadataRef, { maxResults: 1 });
          const returning = res.items.length > 0;

          // Merge flag into existing user object without breaking refs.
          setUser((prev) => ({ ...prev, returning }));
        } catch (err) {
          if (process.env.NODE_ENV === "development") {
            console.error("Error checking returning metadata:", err);
          }
          setUser((prev) => ({ ...prev, returning: false }));
        }
      });
    })();

    return () => {
      cancelled = true;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [auth, consent?.status, consent?.essential, consent]);

  /* ------------------------------------------------------------------ */
  /* Memoise context to keep reference stable between renders.           */
  /* ------------------------------------------------------------------ */
  const contextValue = useMemo(() => ({ user, loading }), [user, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {/* Delay rendering children until first auth resolution completes. */}
      {!loading && children}
    </AuthContext.Provider>
  );
}

/**
 * Convenience hook for consuming the authentication context.
 *
 * @returns {{ user: import('firebase/auth').User | null, loading: boolean }}
 */
export const useAuth = () => useContext(AuthContext);
